import { useState, useEffect, Fragment } from 'react'
import { 
  UserGroupIcon, 
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  AcademicCapIcon,
  ChartBarIcon,
  CalendarIcon,
  XCircleIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'
import api from '../../utils/api'
import InstructorReports from './InstructorReports'

const InstructorManagement = () => {
  const [loading, setLoading] = useState(true)
  const [instructors, setInstructors] = useState([])
  const [assignments, setAssignments] = useState([])
  const [eliteStudents, setEliteStudents] = useState([])
  const [earningsOverview, setEarningsOverview] = useState(null)
  const [activeView, setActiveView] = useState('assignments') // 'instructors', 'assignments', 'assign'
  const [expandedAssignment, setExpandedAssignment] = useState(null)
  const [selectedStudent, setSelectedStudent] = useState('')
  const [selectedInstructor, setSelectedInstructor] = useState('')
  const [assigning, setAssigning] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [instructorsRes, assignmentsRes, studentsRes, earningsRes] = await Promise.all([
        api.get('/admin/instructors'),
        api.get('/admin/instructors/assignments'),
        api.get('/admin/instructors/elite-students'),
        api.get('/admin/instructors/earnings/overview')
      ])

      setInstructors(instructorsRes.data)
      setAssignments(assignmentsRes.data)
      setEliteStudents(studentsRes.data)
      setEarningsOverview(earningsRes.data)
    } catch (error) {
      console.error('Error fetching instructor data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAssignInstructor = async () => {
    if (!selectedStudent || !selectedInstructor) {
      alert('Please select both a student and an instructor')
      return
    }

    try {
      setAssigning(true)
      await api.post('/admin/instructors/assign', {
        student_id: selectedStudent,
        instructor_id: selectedInstructor,
        compensation_amount: 250.00
      })

      alert('Instructor assigned successfully!')
      setSelectedStudent('')
      setSelectedInstructor('')
      setActiveView('assignments')
      fetchData()
    } catch (error) {
      console.error('Error assigning instructor:', error)
      alert('Failed to assign instructor: ' + (error.response?.data?.message || 'Unknown error'))
    } finally {
      setAssigning(false)
    }
  }

  const handleMarkPaid = async (earningId) => {
    const paymentMethod = prompt('Enter payment method (e.g., PayPal, Zelle, Check):')
    if (!paymentMethod) return

    try {
      await api.post(`/admin/instructors/earnings/${earningId}/mark-paid`, {
        payment_method: paymentMethod,
        notes: `Paid on ${new Date().toLocaleDateString()}`
      })

      alert('Earning marked as paid!')
      fetchData()
    } catch (error) {
      console.error('Error marking earning as paid:', error)
      alert('Failed to mark as paid')
    }
  }

  const toggleAssignmentDetails = (assignmentId) => {
    setExpandedAssignment(expandedAssignment === assignmentId ? null : assignmentId)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Earnings Overview */}
      {earningsOverview && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pending</p>
                <p className="text-2xl font-bold text-yellow-600">
                  ${(earningsOverview.total_pending || 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">{earningsOverview.pending_count} payments</p>
              </div>
              <ClockIcon className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">
                  ${(earningsOverview.total_paid || 0).toFixed(2)}
                </p>
              </div>
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Instructors</p>
                <p className="text-2xl font-bold text-blue-600">
                  {earningsOverview.instructors_count}
                </p>
              </div>
              <UserGroupIcon className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Assignments</p>
                <p className="text-2xl font-bold text-purple-600">
                  {earningsOverview.active_assignments}
                </p>
              </div>
              <AcademicCapIcon className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unassigned Elite</p>
                <p className="text-2xl font-bold text-orange-600">
                  {eliteStudents.filter(s => !s.has_instructor).length}
                </p>
              </div>
              <CalendarIcon className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* View Selector */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveView('assignments')}
            className={`${
              activeView === 'assignments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
          >
            <AcademicCapIcon className="w-4 h-4" />
            Assignments ({assignments.length})
          </button>
          <button
            onClick={() => setActiveView('instructors')}
            className={`${
              activeView === 'instructors'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
          >
            <UserGroupIcon className="w-4 h-4" />
            Instructors ({instructors.length})
          </button>
          <button
            onClick={() => setActiveView('assign')}
            className={`${
              activeView === 'assign'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
          >
            <PlusIcon className="w-4 h-4" />
            Assign Instructor
          </button>
          <button
            onClick={() => setActiveView('reports')}
            className={`${
              activeView === 'reports'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
          >
            <ChartBarIcon className="w-4 h-4" />
            Reports & Analytics
          </button>
        </nav>
      </div>

      {/* Assignments View */}
      {activeView === 'assignments' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Instructor-Student Assignments</h2>
            <p className="text-sm text-gray-600 mt-1">Monitor instructor assignments and student progress</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instructor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sessions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earnings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignments.map((assignment) => (
                  <Fragment key={assignment.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{assignment.student.name}</div>
                          <div className="text-sm text-gray-500">{assignment.student.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{assignment.instructor.name}</div>
                          <div className="text-sm text-gray-500">{assignment.instructor.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(assignment.assigned_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${assignment.course_progress.percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-700">{assignment.course_progress.percentage}%</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {assignment.course_progress.completed_modules}/{assignment.course_progress.total_modules} modules
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-green-600 h-2 rounded-full"
                              style={{ width: `${assignment.session_progress.percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-700">
                            {assignment.session_progress.completed}/{assignment.session_progress.total}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            ${(parseFloat(assignment.earning_amount) || 0).toFixed(2)}
                          </div>
                          {assignment.earning_status === 'pending' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                          )}
                          {assignment.earning_status === 'paid' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Paid
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => toggleAssignmentDetails(assignment.id)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          {expandedAssignment === assignment.id ? (
                            <ChevronUpIcon className="w-5 h-5" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5" />
                          )}
                        </button>
                        {assignment.earning_status === 'pending' && (
                          <button
                            onClick={() => handleMarkPaid(assignment.earning_id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedAssignment === assignment.id && (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Student Progress Details</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Total Submissions:</span>
                                  <span className="font-medium">{assignment.course_progress.total_submissions}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Graded Submissions:</span>
                                  <span className="font-medium">{assignment.course_progress.graded_submissions}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Enrolled Since:</span>
                                  <span className="font-medium">
                                    {new Date(assignment.student.enrolled_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Assignment Details</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Status:</span>
                                  <span className="font-medium capitalize">{assignment.status}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Compensation:</span>
                                  <span className="font-medium">${assignment.compensation_amount.toFixed(2)}</span>
                                </div>
                                {assignment.paid_at && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Paid On:</span>
                                    <span className="font-medium">
                                      {new Date(assignment.paid_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {assignments.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No instructor assignments yet
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructors View */}
      {activeView === 'instructors' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Instructor Overview</h2>
            <p className="text-sm text-gray-600 mt-1">All instructors and their performance metrics</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instructor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active Students</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending Earnings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Earned</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {instructors.map((instructor) => (
                  <tr key={instructor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{instructor.name}</div>
                        <div className="text-sm text-gray-500">{instructor.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {instructor.active_students}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-yellow-600">
                      ${(parseFloat(instructor.pending_earnings) || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-green-600">
                      ${instructor.total_earnings.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {instructors.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No instructors found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign Instructor View */}
      {activeView === 'assign' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Assign Instructor to Student</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Elite Student
              </label>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose a student...</option>
                {eliteStudents
                  .filter(s => !s.has_instructor)
                  .map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name} ({student.email})
                    </option>
                  ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Only showing elite students without an instructor
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Instructor
              </label>
              <select
                value={selectedInstructor}
                onChange={(e) => setSelectedInstructor(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose an instructor...</option>
                {instructors.map((instructor) => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.name} ({instructor.active_students} active students)
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Active students shown in parentheses
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-900 mb-2">Assignment Details</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Compensation: $250.00 per student</li>
              <li>• 5 coaching sessions will be created automatically</li>
              <li>• Sessions: 2 Coaching, Code Review, Career Strategy, Portfolio Review</li>
              <li>• Earning record will be created with "pending" status</li>
            </ul>
          </div>

          <button
            onClick={handleAssignInstructor}
            disabled={!selectedStudent || !selectedInstructor || assigning}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {assigning ? 'Assigning...' : 'Assign Instructor'}
          </button>
        </div>
      )}

      {/* Reports View */}
      {activeView === 'reports' && (
        <InstructorReports />
      )}
    </div>
  )
}

export default InstructorManagement
